import React from 'react'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '~/lib/ui/dialog'
import { useCalendlyEventListener, InlineWidget } from 'react-calendly'
import { Button } from '~/lib/ui/button'
import { useCandidate } from './CandidateProvider'
import { useResumeReview } from './ResumeReviewDetail'
import { apiPatch } from '~/util/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  calendlyEventMutation,
  updateLocationDetailItemId,
} from './calendlyMutations'
import { useToast } from '~/lib/ui/toast'
import { Checkbox } from '~/lib/ui/checkbox'

const CoachChangeFormatDialog = ({
  updateCandidateRevision = false,
  currentCandidateRevision = null,
}) => {
  const { candidate, coachId, idRef } = useCandidate()
  const resumeReview = useResumeReview()
  const { toast } = useToast()
  const {
    meetingViaCalendly,
    coachCalendlyLink,
    id: serviceRequestId,
    requestType,
    displayName,
  } = resumeReview || {}
  const [open, setOpen] = React.useState(false)
  const [calendlyOpen, setCalendlyOpen] = React.useState(false)
  const [coachSetsMeeting, setCoachSetsMeeting] = React.useState(false)

  const getFormatDetails = (meetingViaCalendlyValue) => ({
    currentType: meetingViaCalendlyValue ? 'Meeting Based' : 'Electronic Based',
    oppositeType: meetingViaCalendlyValue
      ? 'Electronic Based'
      : 'Meeting Based',
    newFormat: meetingViaCalendlyValue ? 'electronic' : 'calendly',
  })
  const meetingOneDetails = getFormatDetails(meetingViaCalendly)
  const meetingTwoDetails = getFormatDetails(
    currentCandidateRevision?.meetingViaCalendly,
  )
  const currentType = updateCandidateRevision
    ? meetingTwoDetails.currentType
    : meetingOneDetails.currentType
  const oppositeType = updateCandidateRevision
    ? meetingTwoDetails.oppositeType
    : meetingOneDetails.oppositeType
  const newFormat = updateCandidateRevision
    ? meetingTwoDetails.newFormat
    : meetingOneDetails.newFormat

  const meetingDuration = updateCandidateRevision ? 30 : 50

  const currentCandidateRevisionId = currentCandidateRevision?.id

  const queryClient = useQueryClient()

  const showErrorToast = (msg) => {
    toast({
      title: 'Error',
      description: msg,
      variant: 'destructive',
    })
  }

  const showSuccessToast = (msg) => {
    toast({
      title: 'Success',
      description: msg,
    })
  }

  const changeFormatMutation = useMutation({
    mutationFn: async () => {
      try {
        const response = await apiPatch(
          `/coach/dashboard_candidates/${candidate.id}/change_resume_review_format`,
          {
            candidate_id: candidate.id,
            new_format: newFormat,
            update_candidate_revision: updateCandidateRevision,
            coach_sets_meeting: coachSetsMeeting,
          },
        )
        const data = response.data
        return { status: response.status, data }
      } catch (_error) {
        console.log(_error)
        throw new Error('Failed to change review format.')
      }
    },
    onSuccess: () => {
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidate.id])
      showSuccessToast(
        `${displayName || 'Resume Review'} format changed successfully.`,
      )
    },
    onError: (error) => {
      showErrorToast(
        error?.message ||
          `Failed to change ${displayName || 'Resume Review'} format. Please try again.`,
      )
    },
  })

  const createCalendlyEventMutation = useMutation({
    mutationFn: async (payload) => {
      try {
        return await calendlyEventMutation({
          payload,
          candidateId: candidate.id,
          coachId,
          serviceRequestId,
          requestType: updateCandidateRevision
            ? 'ResumeRevision::CandidateRevision'
            : requestType,
          currentCandidateRevisionId,
          meetingFormatChanged: true,
        })
      } catch (_error) {
        console.log(_error)
      }
    },
    onSuccess: (response) => {
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidate.id])
      const newId = response?.service_request?.id
      if (idRef && newId) {
        idRef.current = newId
      }
      if (newId) updateLocationDetailItemId(newId)
    },
    onError: (error) => {
      showErrorToast(
        error?.message ||
          `Failed to reschedule ${displayName || 'Resume Review'} meeting. Please try again.`,
      )
    },
  })

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      const payload = e.data.payload
      createCalendlyEventMutation.mutate(payload)
      changeFormatMutation.mutate()
      setCalendlyOpen(false)
    },
  })

  const handleOpenClick = (e) => {
    e.preventDefault()
    setOpen(true)
  }

  const handleConfirm = () => {
    if (newFormat === 'calendly') {
      if (coachSetsMeeting) {
        setOpen(false)
        setCalendlyOpen(true)
      } else {
        changeFormatMutation.mutate()
      }
    } else {
      changeFormatMutation.mutate()
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild className="cursor-pointer">
          <a href="#" onClick={handleOpenClick}>
            Change to {oppositeType}
          </a>
        </DialogTrigger>
        <DialogContent className="min-h-48">
          <DialogHeader>
            <DialogTitle>Change Resume Review Format</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col space-y-4 px-4 py-2">
            The resume review is currently a {currentType} Review.
            <br />
            Would you like to switch to a {oppositeType} Review?
            {newFormat === 'calendly' && (
              <div className="flex space-x-2">
                <Checkbox
                  id="coach-sets-meeting"
                  checked={coachSetsMeeting}
                  onCheckedChange={(checked) => setCoachSetsMeeting(!!checked)}
                />
                <label
                  htmlFor="coach-sets-meeting"
                  className="cursor-pointer text-sm"
                >
                  I want to set the meeting details myself (otherwise, the
                  candidate will choose a time during their Resume Review
                  process).
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="mb-4">
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={changeFormatMutation.isLoading}
            >
              {changeFormatMutation.isLoading ? 'Updating...' : 'Confirm'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={changeFormatMutation.isLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={calendlyOpen} onOpenChange={setCalendlyOpen}>
        <DialogContent className="min-h-[47rem] min-w-[48rem]">
          <DialogHeader>
            <DialogTitle>Schedule a Meeting for the Candidate</DialogTitle>
          </DialogHeader>
          <InlineWidget
            url={`${coachCalendlyLink}/${meetingDuration}min`}
            prefill={{
              name: candidate?.fullName,
              email: candidate?.email,
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CoachChangeFormatDialog
